import { AudienceIntelligence, Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Kaireka Foods",
  industry: ["Food/Beverages", "Healthy Children's Snacks & Nutrition"],
  sub_industry: ["Children's Healthy Snacks", "Superfood Energy Products", "Kids Nutrition"],
  founded_year: 2023,
  founded_original_name: "Kaireka Foods",
  headquarters: ["Mumbai, India"],
  tagline_primary: "Imagination, Nutrition & Sustainability",
  mission: '"To ignite creativity and build confidence in children through imagination, nutrition, and sustainability."',
  vision: '"To make snacking enjoyable and healthful for kids worldwide through real, creative foods."',
  overview:
    "Kaireka Foods (Kaidoo Kids) is a children's snack brand focused on natural ingredients and space-themed branding. Products include Choco Comets energy balls and Cosmix mixes. It sells in the US via Amazon and direct-to-consumer channels.",
  scale: {
    revenue: 0,
    employees_estimate: 10,
    markets_served_count: 2,
    operating_countries_count: 2,
  },
  global_presence: {
    regions: [
      {
        region: "India / South Asia",
        headquarters: "Mumbai, India",
        active_markets: ["India"],
        market_type: "Direct-to-Consumer, E-commerce",
      },
      {
        region: "United States / North America",
        headquarters: "Not found",
        active_markets: ["United States"],
        market_type: "E-commerce, Amazon marketplace, Direct-to-Consumer",
      },
    ],
    key_markets_by_revenue_share: [],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "Space Purple",
          hex: "#6B4C9A",
        },
        {
          name: "Cosmic Blue",
          hex: "#4A90E2",
        },
        {
          name: "Galaxy Black",
          hex: "#1A1A2E",
        },
      ],
      secondary: [
        {
          name: "Stellar Orange",
          hex: "#FF8C42",
        },
        {
          name: "Mint Green",
          hex: "#7FD8BE",
        },
        {
          name: "Rocket Red",
          hex: "#E74C3C",
        },
      ],
      accent: [
        {
          name: "Sunshine Yellow",
          hex: "#FFC857",
        },
        {
          name: "Comet White",
          hex: "#FFFFFF",
        },
        {
          name: "Nebula Gray",
          hex: "#95A5A6",
        },
      ],
    },
    typography: [
      {
        font_family: "Sans-serif",
        weight: "Bold, Semi-Bold",
        usage: "Headlines, product names, logo lockup",
      },
      {
        font_family: "Sans-serif",
        weight: "Regular, Medium",
        usage: "Body text, product descriptions, nutritional information",
      },
      {
        font_family: "Playful display font",
        weight: "Bold",
        usage: "Activity sheets, packaging callouts, fun messaging",
      },
    ],
    logos: [
      {
        variant: "Primary",
        url: "https://kaidookids.com/cdn/shop/files/logo.svg?v=1750766577&width=330",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Playful", "Imaginative", "Trustworthy", "Educational", "Adventurous", "Warm & Inclusive", "Fun & Energetic"],
    taglines: [
      "Fuel a Multiverse of Imagination with Kaidoo Kids!",
      "We fuel imagination through nutrition!",
      "Imagination, Nutrition & Sustainability",
      "Choco Comets: Perfect cosmic delights for young astronauts captivated by space adventures!",
      "We make super tasty, fun & healthy foods for our little explorers!",
      "The Kaidooverse has Fun Activities, a Unique Kids Hosted Podcast, Yummy Recipes for healthy Kids' Snacks and much more!",
    ],
    messaging_dos: [],
    messaging_donts: [],
    banned_words: ["junk food", "candy", "artificial", "processed", "unhealthy", "guilt"],
  },
  creative_assets: [
    "https://assets.zocket.com/uploads/cefbd684-c6fe-4404-aebc-3955872d9458-Screenshot_2026-02-11_at_4.47.36_PM.png",
    "https://assets.zocket.com/uploads/92633674-c892-4c1e-9bff-f46165eb10b9-Screenshot_2026-02-11_at_4.48.56_PM.png",
    "https://assets.zocket.com/uploads/371bdd5c-5b80-4446-b438-541fe45767f5-Screenshot_2026-02-11_at_4.49.50_PM.png",
  ],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  brand_architecture: {
    available_brands: ["Kaidoo Kids"],
    selected_brand: "Kaidoo Kids",
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "94%",
      patterns_learned: 342,
      avg_generation: "1.5s",
    },
    style_drift_alerts: [
      {
        title: "Kaidoo Kids Logo",
        description: "Logo placement inconsistent with brand guidelines",
        severity: "medium",
      },
    ],
    preference_learning: [
      { preference: "Playful & Fun", score: 92, change: 5 },
      { preference: "Space-Themed", score: 88, change: 4 },
      { preference: "Natural & Healthy", score: 86, change: 3 },
      { preference: "Family-Focused", score: 82, change: 6 },
    ],
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 7.8,
    sentiment_graph: [
      {
        metric: "Sentiment Score",
        value: "78%",
        delta: "New Brand",
      },
      {
        metric: "Product Quality Rating",
        value: "~4.0/5",
        delta: "Limited Data",
      },
      {
        metric: "Market Category Growth",
        value: "+6.2%",
        delta: "+0.4% YoY",
      },
      {
        metric: "Healthy Kids Snacks Market Size",
        value: "$6.93B",
        delta: "+5.6% CAGR",
      },
      {
        metric: "North America Market Share",
        value: "<0.1%",
        delta: "Emerging",
      },
      {
        metric: "Amazon Best Seller Rank",
        value: "#102K",
        delta: "Variable",
      },
      {
        metric: "Customer Retention (Est.)",
        value: "~65%",
        delta: "Early Stage",
      },
      {
        metric: "Brand Awareness",
        value: "<1%",
        delta: "Building",
      },
    ],
  },
  sentiment_summary: {
    time_range: ["2024", "2025"],
    themes: {
      positive: [
        "Natural & Clean Ingredients",
        "Nutritional Value",
        "Kid Appeal & Taste",
        "Unique Space Theme & Activities",
        "Convenience & Portability",
        "Picky Eater Solution",
        "Sustainability Focus",
      ],
      neutral: ["Price Point Perception", "Limited Availability", "Brand Awareness"],
      negative: ["Taste Preferences", "Texture Concerns", "Limited Product Range", "Market Penetration"],
    },
  },
  distribution: [
    {
      brand: "General Mills (Annie's Homegrown)",
      share: 18.5,
    },
    {
      brand: "Nestlé (Gerber, NutriPuffs)",
      share: 16.2,
    },
    {
      brand: "Mondelez International",
      share: 14.8,
    },
    {
      brand: "Kraft Heinz (Plum Organics)",
      share: 12.3,
    },
    {
      brand: "Others",
      share: 38.2,
    },
  ],
  sentiment_by_product: [
    {
      product: "Choco Comets - Double Chocolate",
      sentiment: 75,
      change_delta: 2,
    },
    {
      product: "Choco Comets - Mint Crunch",
      sentiment: 82,
      change_delta: 3,
    },
    {
      product: "Choco Comets - Crunchy Orange",
      sentiment: 80,
      change_delta: 1,
    },
    {
      product: "Choco Comets - Yummy Berry",
      sentiment: 78,
      change_delta: 2,
    },
    {
      product: "Choco Comets - Blueberry Blast",
      sentiment: 76,
      change_delta: 0,
    },
    {
      product: "Choco Comets - Peanut Butter",
      sentiment: 68,
      change_delta: 4,
    },
    {
      product: "Cosmix Pancake & Waffle Mix",
      sentiment: 72,
      change_delta: 3,
    },
    {
      product: "Explorers Milk Mix",
      sentiment: 70,
      change_delta: 2,
    },
  ],
  top_topics: {
    praise: [
      {
        topic: "Natural, Clean Ingredients",
        weight: 10,
      },
      {
        topic: "High Nutritional Value",
        weight: 10,
      },
      {
        topic: "Kid-Friendly Taste",
        weight: 10,
      },
      {
        topic: "Picky Eater Success",
        weight: 8,
      },
      {
        topic: "Interactive Packaging & Activities",
        weight: 8,
      },
      {
        topic: "Convenience & Portability",
        weight: 7,
      },
      {
        topic: "Sustainability & Eco-Consciousness",
        weight: 7,
      },
    ],
    complaints: [
      {
        topic: "Limited Retail Availability",
        weight: 5,
      },
      {
        topic: "Taste Preferences (Specific Flavors)",
        weight: 4,
      },
      {
        topic: "Texture Concerns",
        weight: 3,
      },
      {
        topic: "Premium Pricing",
        weight: 2,
      },
      {
        topic: "Limited Product Variety",
        weight: 2,
      },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    {
      name: "Once Upon a Farm",
      threat_level: "high",
      market_share: 4,
      sentiment: 78,
    },
    {
      name: "Happy Family Organics",
      threat_level: "high",
      market_share: 10,
      sentiment: 75,
    },
    {
      name: "Cerebelly",
      threat_level: "medium-high",
      market_share: 2.5,
      sentiment: 80,
    },
    {
      name: "Plum Organics",
      threat_level: "high",
      market_share: 7,
      sentiment: 72,
    },
    {
      name: "Serenity Kids",
      threat_level: "medium",
      market_share: 1.5,
      sentiment: 76,
    },
    {
      name: "Ella's Kitchen",
      threat_level: "medium",
      market_share: 5,
      sentiment: 74,
    },
    {
      name: "Perfect Bar",
      threat_level: "medium",
      market_share: 2.5,
      sentiment: 70,
    },
    {
      name: "RXBAR Kids",
      threat_level: "medium",
      market_share: 3.5,
      sentiment: 73,
    },
  ],
  secondary_competitors: [
    {
      name: "KIND Snacks",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "CLIF Kid",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Annie's Homegrown",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "GoGo squeeZ",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Larabar",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
    {
      name: "Sprout Organic Foods",
      threat_level: "medium",
      market_share: 0,
      sentiment: 0,
    },
  ],
  share_of_voice: [
    {
      channel: "Social Media",
      distribution: {
        "Happy Family Organics": 20,
        "Plum Organics": 13,
        "Once Upon a Farm": 18,
        "Gerber Organic": 18,
        "Ella's Kitchen": 10,
        "Kaidoo Kids": 20,
        Others: 31,
      },
    },
    {
      channel: "Retail/In-Store Presence",
      distribution: {
        "Happy Family Organics": 23,
        "Plum Organics": 16,
        "Once Upon a Farm": 14,
        "Gerber Organic": 28,
        "Ella's Kitchen": 9,
        "Kaidoo Kids": 15,
        Others: 9,
      },
    },
    {
      channel: "Digital/Search",
      distribution: {
        "Happy Family Organics": 20,
        "Plum Organics": 14,
        "Once Upon a Farm": 12,
        "Gerber Organic": 18,
        "Ella's Kitchen": 10,
        "Kaidoo Kids": 20,
        Others: 27,
      },
    },
    {
      channel: "E-commerce/Amazon",
      distribution: {
        "Happy Family Organics": 18,
        "Plum Organics": 14,
        "Once Upon a Farm": 10,
        "Gerber Organic": 9,
        "Ella's Kitchen": 7,
        "Kaidoo Kids": 19,
        Others: 41,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Brand Awareness - Aided Recognition Among Parents of Children 3-12",
      brands: [
        {
          brand_name: "Happy Family Organics",
          score: 82,
        },
        {
          brand_name: "Plum Organics",
          score: 78,
        },
        {
          brand_name: "Once Upon a Farm",
          score: 72,
        },
        {
          brand_name: "Gerber Organic",
          score: 88,
        },
        {
          brand_name: "Ella's Kitchen",
          score: 58,
        },
        {
          brand_name: "Kaidoo Kids",
          score: 5,
        },
      ],
    },
    {
      metric_name: "Customer Satisfaction Index - Parent Ratings",
      brands: [
        {
          brand_name: "Once Upon a Farm",
          score: 82,
        },
        {
          brand_name: "Happy Family Organics",
          score: 80,
        },
        {
          brand_name: "Plum Organics",
          score: 76,
        },
        {
          brand_name: "Ella's Kitchen",
          score: 78,
        },
        {
          brand_name: "Kaidoo Kids",
          score: 0,
        },
        {
          brand_name: "Gerber Organic",
          score: 74,
        },
      ],
    },
    {
      metric_name: "Net Promoter Score (Scaled to 0-100)",
      brands: [
        {
          brand_name: "Once Upon a Farm",
          score: 78,
        },
        {
          brand_name: "Gerber Organic",
          score: 80,
        },
        {
          brand_name: "Plum Organics",
          score: 72,
        },
        {
          brand_name: "Ella's Kitchen",
          score: 74,
        },
        {
          brand_name: "Kaidoo Kids",
          score: 0,
        },
        {
          brand_name: "Happy Family Organics",
          score: 76,
        },
      ],
    },
    {
      metric_name: "Clean Label & Safety Certification Score",
      brands: [
        {
          brand_name: "Gerber Organic",
          score: 95,
        },
        {
          brand_name: "Happy Family Organics",
          score: 88,
        },
        {
          brand_name: "Once Upon a Farm",
          score: 85,
        },
        {
          brand_name: "Plum Organics",
          score: 80,
        },
        {
          brand_name: "Ella's Kitchen",
          score: 82,
        },
        {
          brand_name: "Kaidoo Kids",
          score: 85,
        },
      ],
    },
    {
      metric_name: "Product Innovation & Unique Value Proposition",
      brands: [
        {
          brand_name: "Kaidoo Kids",
          score: 88,
        },
        {
          brand_name: "Once Upon a Farm",
          score: 82,
        },
        {
          brand_name: "Gerber Organic",
          score: 85,
        },
        {
          brand_name: "Ella's Kitchen",
          score: 80,
        },
        {
          brand_name: "Happy Family Organics",
          score: 75,
        },
        {
          brand_name: "Plum Organics",
          score: 83,
        },
      ],
    },
    {
      metric_name: "Retail Distribution & Accessibility",
      brands: [
        {
          brand_name: "Happy Family Organics",
          score: 95,
        },
        {
          brand_name: "Plum Organics",
          score: 92,
        },
        {
          brand_name: "Once Upon a Farm",
          score: 88,
        },
        {
          brand_name: "Gerber Organic",
          score: 98,
        },
        {
          brand_name: "Ella's Kitchen",
          score: 75,
        },
        {
          brand_name: "Kaidoo Kids",
          score: 15,
        },
      ],
    },
  ],
  positioning: {
    competitive_advantages: ["Premium healthy snacks with space-themed experiential branding", "Targeting children ages 3-12", "Sustainability focus"],
    improvement_areas: ["Limited market presence", "Low brand awareness", "Minimal social media following"],
    disruption_watchlist: ["Yumi", "Little Spoon", "Raised Real", "Amara Organic", "Odd Balls"],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "Kaidoo Kids targets millennial parents, particularly mothers, who prioritize healthy snacks for their children. The audience is predominantly female, with a significant portion having a bachelor's degree or higher. They are health-conscious, value transparency, and prefer convenience in their shopping habits. The audience is digitally native, with a strong presence on social media platforms like Instagram and Facebook.",
  core_demographics: {
    primary_age_range: "25-34",
    gender_split: {
      female: 70,
      male: 30,
    },
    demographics_distribution: {
      age: [
        { age_range: "5-12", percentage: 30 },
        { age_range: "13-18", percentage: 10 },
        { age_range: "25-34", percentage: 60 },
      ],

      education: [
        { education_level: "School", percentage: 50 },
        { education_level: "College", percentage: 30 },
        { education_level: "Graduate Degree", percentage: 20 },
      ],
      geographic: [
        { location: "Urban", percentage: 30 },
        { location: "Suburban", percentage: 40 },
        { location: "Rural", percentage: 30 },
      ],
    },
    income_median_usd: 65000,
    location_type: "Suburban",
  },
  occupation_distribution: [
    { occupation: "Homemakers", percentage: 50 },
    { occupation: "IT Professionals", percentage: 30 },
    { occupation: "Caregivers", percentage: 20 },
  ],
  interests: {
    top_categories: [
      {
        category: "Parenting & Child Development",
        score: 9.5,
      },
      {
        category: "Health & Wellness",
        score: 9,
      },
      {
        category: "Organic/Natural Living",
        score: 8.5,
      },
      {
        category: "Cooking & Meal Planning",
        score: 8,
      },
      {
        category: "Education & Learning",
        score: 7.5,
      },
      {
        category: "Sustainability & Environment",
        score: 7,
      },
      {
        category: "Technology & Apps",
        score: 7,
      },
      {
        category: "Family Activities",
        score: 6.5,
      },
      {
        category: "Fitness & Active Living",
        score: 6,
      },
      {
        category: "Social Media & Influencers",
        score: 6,
      },
    ],
    brand_affinities: [
      {
        brand: "Annie's Homegrown",
        affinity: 72,
      },
      {
        brand: "Goldfish (Pepperidge Farm)",
        affinity: 68,
      },
      {
        brand: "Nature Valley",
        affinity: 65,
      },
      {
        brand: "GoGo SqueeZ",
        affinity: 62,
      },
      {
        brand: "Clif Kid",
        affinity: 58,
      },
      {
        brand: "Happy Baby/Happy Tot",
        affinity: 55,
      },
      {
        brand: "Once Upon a Farm",
        affinity: 52,
      },
      {
        brand: "RXBAR Kids",
        affinity: 48,
      },
      {
        brand: "Made Good",
        affinity: 45,
      },
      {
        brand: "Little Spoon",
        affinity: 42,
      },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        "Amazon/Online Marketplace": 43.3,
        "Direct-to-Consumer (Brand Website)": 19.9,
        "Grocery/Supermarkets": 21.9,
        "Natural/Health Food Stores": 9,
        "Club Stores (Costco/Sam's)": 6,
      },
      purchase_frequency: "2.5 times per month",
      avg_order_value_usd: 38.5,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Problem Recognition",
          description: "Parent seeks healthier snack alternatives; discovers through social media, Amazon search, or peer recommendations",
          duration_days: 5,
        },
        {
          stage: 2,
          name: "Information Gathering",
          description: "Reads ingredient labels, reviews nutritional content, checks customer reviews; compares with other healthy snack brands",
          duration_days: 7,
        },
        {
          stage: 3,
          name: "Trial Decision",
          description: "Assesses price vs. value, considers child's taste preferences, evaluates convenience factors (packaging, shelf-life)",
          duration_days: 3,
        },
        {
          stage: 4,
          name: "First Purchase",
          description: "Makes initial trial purchase (smaller quantity); often through Amazon Prime for fast delivery",
          duration_days: 1,
        },
        {
          stage: 5,
          name: "Repeat Evaluation",
          description: "Child taste test, monitors nutritional impact, decides on subscription or repeat purchase",
          duration_days: 17,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        "Mobile (Smartphone)": 55.1,
        "Desktop/Laptop": 30.6,
        Tablet: 14.3,
      },
      media_channels: {
        Instagram: 27.6,
        Facebook: 22.1,
        "Amazon (Product Discovery)": 18.4,
        YouTube: 12.4,
        Pinterest: 8.3,
        TikTok: 6.5,
        "Email/Newsletter": 4.6,
      },
    },
  },
  platforms: {
    distribution: {
      Instagram: 27.6,
      Facebook: 22.1,
      Amazon: 18.4,
      YouTube: 12.4,
      Pinterest: 8.3,
      TikTok: 6.5,
      Email: 4.6,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    {
      platform: "Instagram",
      followers: 1703,
      health_score: 50,
    },
    {
      platform: "Pinterest",
      followers: 4,
      health_score: 30,
    },
    {
      platform: "LinkedIn",
      followers: 20,
      health_score: 40,
    },
    {
      platform: "Facebook",
      followers: 0,
      health_score: 20,
    },
    {
      platform: "YouTube",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "TikTok",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "Twitter/X",
      followers: 0,
      health_score: 0,
    },
    {
      platform: "SlideShare",
      followers: 0,
      health_score: 20,
    },
  ],
  sentiment_distribution: [
    {
      label: "Positive",
      percentage: 40,
    },
    {
      label: "Neutral",
      percentage: 31,
    },
    {
      label: "Negative",
      percentage: 29,
    },
  ],
  recent_comments: [
    {
      user: "foodie_lover",
      platform: "Instagram",
      timeAgo: "2 days ago",
      status: "positive",
      commentText: "Love the new Choco Comets!",
      postContext: "Before & After post",
    },
    {
      user: "health_enthusiast",
      platform: "Instagram",
      timeAgo: "1 week ago",
      status: "neutral",
      commentText: "Not sure about the ingredients.",
      postContext: "Corporate communication post",
    },
  ],
  engagement: {
    engagement_by_platform: [
      {
        platform: "Instagram",
        engagement_rate: 1,
      },
      {
        platform: "Pinterest",
        engagement_rate: 35,
      },
      {
        platform: "LinkedIn",
        engagement_rate: 10,
      },
    ],
    best_performing_content_types: [
      {
        content_type: "Reels",
        performance: 9,
      },
      {
        content_type: "Carousel Posts",
        performance: 8,
      },
      {
        content_type: "Product Photography",
        performance: 6,
      },
      {
        content_type: "Educational Content",
        performance: 7,
      },
      {
        content_type: "User-Generated Content (UGC)",
        performance: 1,
      },
      {
        content_type: "Stories",
        performance: 5,
      },
      {
        content_type: "Behind-the-Scenes/Brand Story",
        performance: 4,
      },
    ],
    follower_growth_trend: [
      {
        month: "September",
        followers: 574,
      },
      {
        month: "October",
        followers: 403,
      },
      {
        month: "November",
        followers: 343,
      },
      {
        month: "December",
        followers: 700,
      },
      {
        month: "January",
        followers: 1503,
      },
      {
        month: "February",
        followers: 1703,
      },
    ],
  },
};

export const mBrandIntelligenceKaidoo: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
